export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()

  // 使用全域變數來儲存實例
  if (!window.$tonConnector) {
    window.$tonConnector = {
      instance: null,
      connectedWallet: null,
      loadingPromise: null
    }
  }

  // 檢查 SDK 是否存在
  const checkSDK = () => {
    return typeof window !== 'undefined' && window.TON_CONNECT_UI
  }

  // 優化 SDK 載入檢查（使用輪詢代替 MutationObserver 避免記憶體洩漏）
  const waitForTonConnect = () => {
    if (window.$tonConnector.loadingPromise) {
      return window.$tonConnector.loadingPromise
    }

    window.$tonConnector.loadingPromise = new Promise((resolve, reject) => {
      // 如果已經載入，直接返回
      if (checkSDK()) {
        resolve(window.TON_CONNECT_UI)
        return
      }

      // 使用輕量級輪詢代替 MutationObserver
      const POLL_INTERVAL = 100
      const TIMEOUT = 10000
      const startTime = Date.now()

      const checkInterval = setInterval(() => {
        if (checkSDK()) {
          clearInterval(checkInterval)
          resolve(window.TON_CONNECT_UI)
          return
        }

        if (Date.now() - startTime >= TIMEOUT) {
          clearInterval(checkInterval)
          reject(new Error('TON Connect UI SDK 載入超時'))
        }
      }, POLL_INTERVAL)
    })

    return window.$tonConnector.loadingPromise
  }

  function getConnector() {
    try {
      if (!window.$tonConnector.instance) {
        if (!checkSDK()) {
          return null
        }

        // 檢查並清理已存在的自定義元素
        try {
          if (customElements.get('tc-root')) {
            // 如果元素已存在，先移除所有相關元素
            document.querySelectorAll('tc-root').forEach(el => el.remove())
          }
        } catch (e) {
          console.warn('清理 tc-root 元素失敗:', e)
        }

        const { resourceId, miniAppLink } = config.public
        const currentDomain = window.location.hostname === 'localhost' ? 'http://localhost:3000' : `https://${window.location.hostname}`

        window.$tonConnector.instance = new window.TON_CONNECT_UI.TonConnectUI({
          manifestUrl: `${currentDomain}/share/${resourceId}/tonconnect-manifest.json`,
          uiPreferences: {
            theme: 'SYSTEM'
          }
        })

        window.$tonConnector.instance.uiOptions = {
          twaReturnUrl: miniAppLink
        }

        window.$tonConnector.instance.onStatusChange((walletInfo) => {
          window.$tonConnector.connectedWallet = walletInfo || null
        })
      }

      return window.$tonConnector.instance
    } catch (error) {
      console.error('初始化 TON Connect 失敗:', error)
      return null
    }
  }

  function clearConnector() {
    if (window.$tonConnector.instance) {
      try {
        window.$tonConnector.instance.disconnect()
      } catch (e) {
        console.warn('斷開連接失敗:', e)
      }
      window.$tonConnector.instance = null
      window.$tonConnector.connectedWallet = null
    }
  }

  async function connectWallet() {
    try {
      if (!window.$tonConnector.instance) {
        await waitForTonConnect()
        window.$tonConnector.instance = getConnector()
      }

      if (!window.$tonConnector.instance) {
        throw new Error('無法初始化連接器')
      }

      if (!window.$tonConnector.connectedWallet) {
        await window.$tonConnector.instance.openModal()
        window.$tonConnector.connectedWallet = await window.$tonConnector.instance.connectWallet()
      }
      
      return window.$tonConnector.connectedWallet
    } catch (error) {
      console.error('連接錢包失敗:', error)
      throw error
    }
  }

  function getConnectedWallet() {
    return window.$tonConnector.connectedWallet
  }

  async function walletTransaction(transaction) {
    try {
      if (!window.$tonConnector.instance) {
        throw new Error('連接器未初始化')
      }

      if (!window.$tonConnector.connectedWallet) {
        throw new Error('錢包未連接')
      }

      const result = await window.$tonConnector.instance.sendTransaction(transaction)
      return result
    } catch (error) {
      console.error('交易失敗:', error)
      throw error
    }
  }

  async function getWalletInfo() {
    try {
      if (!window.$tonConnector.instance || !window.$tonConnector.connectedWallet) {
        return null
      }
      
      const { runningEnv } = config.public
      return {
        address: window.$tonConnector.connectedWallet.account.address,
        balance: window.$tonConnector.connectedWallet.account.balance || '0',
        network: window.$tonConnector.connectedWallet.account.chain || 'mainnet',
        connected: true,
        publicKey: window.$tonConnector.connectedWallet.account.publicKey
      }
    } catch (error) {
      console.error('獲取錢包資訊失敗:', error)
      return null
    }
  }

  async function disconnect() {
    try {
      if (window.$tonConnector.instance) {
        await window.$tonConnector.instance.disconnect()
        window.$tonConnector.connectedWallet = null
      }
    } catch (error) {
      console.error('斷開連接失敗:', error)
      throw error
    }
  }

  async function openModal() {
    try {
      if (!window.$tonConnector.instance) {
        await waitForTonConnect()
        window.$tonConnector.instance = getConnector()
      }

      if (window.$tonConnector.instance) {
        await window.$tonConnector.instance.openModal()
      }
    } catch (error) {
      console.error('開啟連接視窗失敗:', error)
      throw error
    }
  }

  function closeModal() {
    try {
      if (window.$tonConnector.instance) {
        window.$tonConnector.instance.closeModal()
      }
    } catch (error) {
      console.error('關閉連接視窗失敗:', error)
    }
  }

  // 注入方法
  return {
    provide: {
      tonWallet: {
        getConnector,
        getConnectedWallet,
        connectWallet,
        walletTransaction,
        clearConnector,
        getWalletInfo,
        disconnect,
        openModal,
        closeModal
      }
    }
  }
})